/* RESPONSIBLE TEAM: team-inbox */
/* eslint-disable no-console */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import type Conversation from 'embercom/objects/inbox/conversation';
import { ConversationState } from 'embercom/objects/inbox/conversation';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import { StateChangeType } from 'embercom/models/data/inbox/state-change-enums';
import type TeammateStateChange from 'embercom/objects/inbox/renderable/state-changes/teammate-state-change';
import storage from 'embercom/vendor/intercom/storage';
import { postRequest } from 'embercom/lib/inbox/requests';
import type LogService from 'embercom/services/log-service';
import type InboxState from 'embercom/services/inbox-state';
import type AdminAwayService from 'embercom/services/admin-away-service';
import { task } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';

export enum HandlingEventType {
  Resumed = 'resumed',
  Paused = 'paused',
}

export enum PauseReason {
  Switch = 'switch',
  Close = 'close',
  Snooze = 'snooze',
  Reassign = 'reassign',
  AutoAway = 'auto_away',
}

export interface HandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: Date;
  reason?: PauseReason;
}

interface StoredHandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: string;
  reason?: PauseReason;
}

const STORAGE_KEY_PREFIX = 'inbox.handling_events';
const ACTIVE_CONVERSATION_KEY_PREFIX = 'inbox.active_handling_conversation';

export default class ConversationHandlingService extends Service {
  @service declare session: Session;
  @service declare logService: LogService;
  @service declare inboxState: InboxState;
  @service declare adminAwayService: AdminAwayService;

  @tracked activeConversation: Conversation | null = null;
  @tracked pendingEvents: HandlingEvent[] = [];

  private flushEventsViaSendBeaconBound!: () => void;
  private handleAutoPauseOnAwayBound!: () => void;

  get storageKey(): string {
    return `${STORAGE_KEY_PREFIX}-${this.session.workspace.id}-${this.session.teammate.id}`;
  }

  get activeConversationStorageKey(): string {
    return `${ACTIVE_CONVERSATION_KEY_PREFIX}-${this.session.workspace.id}-${this.session.teammate.id}`;
  }

  constructor() {
    super(...arguments);
    this.loadFromStorage();
    this.flushEventsViaSendBeaconBound = this.flushEventsViaSendBeacon.bind(this);
    this.handleAutoPauseOnAwayBound = this.handleAutoPauseOnAway.bind(this);

    window.addEventListener('pagehide', this.flushEventsViaSendBeaconBound);
    window.addEventListener('focus', this.handleAutoPauseOnAwayBound);
  }

  willDestroy(): void {
    super.willDestroy();
    window.removeEventListener('pagehide', this.flushEventsViaSendBeaconBound);
    window.removeEventListener('focus', this.handleAutoPauseOnAwayBound);
  }

  private loadFromStorage(): void {
    try {
      let eventsJson = storage.get(this.storageKey);
      if (eventsJson) {
        let storedEvents = JSON.parse(eventsJson) as StoredHandlingEvent[];
        this.pendingEvents = storedEvents.map((e) => ({
          conversationId: e.conversationId,
          type: e.type,
          timestamp: new Date(e.timestamp),
          reason: e.reason,
        }));
      }
    } catch (error) {
      console.error('[AHT] Failed to load from storage', error);
      this.clearEventsStorage();
    }
  }

  private saveToStorage(): void {
    try {
      let storedEvents: StoredHandlingEvent[] = this.pendingEvents.map((e) => ({
        conversationId: e.conversationId,
        type: e.type,
        timestamp: e.timestamp.toISOString(),
        reason: e.reason,
      }));
      storage.set(this.storageKey, JSON.stringify(storedEvents));

      // Save active conversation ID (per-tab via sessionStorage)
      if (this.activeConversation?.id) {
        sessionStorage.setItem(
          this.activeConversationStorageKey,
          String(this.activeConversation.id),
        );
      } else {
        sessionStorage.removeItem(this.activeConversationStorageKey);
      }
    } catch (error) {
      console.error('[AHT] Failed to save to storage', error);
    }
  }

  private clearEventsStorage(): void {
    storage.remove(this.storageKey);
    this.pendingEvents = [];
  }

  private shouldTrackConversation(conversation: Conversation): boolean {
    let isOpen = conversation.state === ConversationState.Open;
    let isAssignedToMe = conversation.adminAssignee?.id === this.session.teammate.id;
    let isValidType =
      !conversation.isBackOfficeTicket &&
      !conversation.isSideConversation &&
      !conversation.isTrackerTicket;

    return isOpen && isAssignedToMe && isValidType;
  }

  private alreadyResumed(conversationId: number): boolean {
    let activeMatch = this.activeConversation?.id === conversationId;
    let storedConvId = sessionStorage.getItem(this.activeConversationStorageKey);
    let storageMatch = storedConvId !== null && Number(storedConvId) === conversationId;
    return activeMatch || storageMatch;
  }

  private clearStoredActiveIfMatches(conversationId: number): void {
    let storedConvId = sessionStorage.getItem(this.activeConversationStorageKey);
    if (storedConvId !== null && Number(storedConvId) === conversationId) {
      sessionStorage.removeItem(this.activeConversationStorageKey);
    }
  }

  // Get timestamp from last part if it's an assignment or relevant state change event
  private getLastTrackableEventTimestamp(conversation: Conversation): Date | undefined {
    // Check if conversation has parts before accessing the getter
    if (!conversation.committedParts?.length && !conversation.pendingParts?.length) {
      return undefined;
    }

    let lastPart = conversation.lastPart;
    if (!lastPart) {
      return undefined;
    }

    // Skip optimistic (pending) parts — only use server-committed timestamps
    if (conversation.pendingParts?.includes(lastPart)) {
      return undefined;
    }

    let isTrackableEvent =
      lastPart.renderableType === RenderableType.Assignment ||
      (lastPart.renderableType === RenderableType.StateChange &&
        ((lastPart.renderableData as TeammateStateChange).change === StateChangeType.Opened ||
          (lastPart.renderableData as TeammateStateChange).change === StateChangeType.Unsnoozed));

    return isTrackableEvent ? lastPart.createdAt : undefined;
  }

  private createResumeEvent(conversation: Conversation, timestamp?: Date): void {
    let event: HandlingEvent = {
      conversationId: conversation.id,
      type: HandlingEventType.Resumed,
      timestamp: timestamp ?? new Date(),
    };

    this.pendingEvents.push(event);
    console.log(
      `[AHT] ▶️ RESUME conversation=${conversation.id} admin=${this.session.teammate.id} timestamp=${event.timestamp.toISOString()}`,
    );
  }

  private createPauseEvent(conversationId: number, reason?: PauseReason, timestamp?: Date): void {
    let event: HandlingEvent = {
      conversationId,
      type: HandlingEventType.Paused,
      timestamp: timestamp ?? new Date(),
      reason,
    };

    this.pendingEvents.push(event);
    console.log(
      `[AHT] ⏸️ PAUSE conversation=${conversationId} reason=${reason || 'none'} admin=${this.session.teammate.id} timestamp=${event.timestamp.toISOString()}`,
    );
  }

  private shouldAutoPauseOnAway(): boolean {
    // Check if auto-pause on away is enabled, there's an active conversation, and we have auto-changed timestamp
    if (
      !this.adminAwayService.autoPauseOnAwayEnabled ||
      !this.activeConversation ||
      !this.adminAwayService.autoChangedTime
    ) {
      return false;
    }

    let autoChangedTime = new Date(this.adminAwayService.autoChangedTime);

    // Get the last handling event to check timing
    let lastEvent = this.pendingEvents[this.pendingEvents.length - 1];
    if (lastEvent && autoChangedTime < lastEvent.timestamp) {
      return false;
    }

    return true;
  }

  // Called when window gains focus (user returns to window or wakes from sleep)
  private handleAutoPauseOnAway(): void {
    if (!this.shouldAutoPauseOnAway()) {
      return;
    }

    let conversationToPause = this.activeConversation!;
    let autoChangedTime = new Date(this.adminAwayService.autoChangedTime);

    console.log(`[AHT] Admin was away, pausing conversation ${conversationToPause.id}`);
    this.createPauseEvent(conversationToPause.id, PauseReason.AutoAway, autoChangedTime);
    this.createResumeEvent(conversationToPause, new Date());
    this.saveToStorage();
  }

  // Called only when switching between conversations
  // Only tracks movements between trackable conversations
  // Non-trackable conversations are ignored - we keep tracking the previous trackable one
  handleConversationSwitch(conversation: Conversation): void {
    let isNewTrackable = this.shouldTrackConversation(conversation);

    // If not trackable, clear storage if it has this conversation (prevents stale storage blocking future resumes)
    if (!isNewTrackable) {
      this.clearStoredActiveIfMatches(conversation.id);
      return;
    }

    // Skip if already tracking this conversation
    if (this.alreadyResumed(conversation.id)) {
      return;
    }

    // Pause previous conversation if switching to a different one
    // Check both in-memory activeConversation AND sessionStorage (in case of page reload)
    let storedConvId = sessionStorage.getItem(this.activeConversationStorageKey);
    let activeConversationId =
      this.activeConversation?.id ?? (storedConvId && Number(storedConvId));

    if (activeConversationId && activeConversationId !== conversation.id) {
      // If we have the full conversation object in memory, check if it's still trackable
      if (this.activeConversation && this.shouldTrackConversation(this.activeConversation)) {
        this.createPauseEvent(this.activeConversation.id, PauseReason.Switch, new Date());
      } else if (!this.activeConversation) {
        // After page reload, we only have the ID from storage, so pause it
        this.createPauseEvent(activeConversationId, PauseReason.Switch, new Date());
      }
    }

    // Resume the new trackable conversation
    this.createResumeEvent(conversation);
    this.activeConversation = conversation;
    this.saveToStorage();
  }

  // Called when conversation parts change (lastPart updates)
  // If conversation becomes trackable, resume it.
  // If untrackable, clear active so we can resume later.
  handleStateOrAssignmentChange(conversation: Conversation): void {
    let isTrackable = this.shouldTrackConversation(conversation);
    let alreadyResumed = this.alreadyResumed(conversation.id);

    if (isTrackable && !alreadyResumed) {
      let timestamp = this.getLastTrackableEventTimestamp(conversation);

      // Skip if no trackable event timestamp (lastPart is optimistic or not a trackable event type)
      if (!timestamp) {
        return;
      }

      if (this.activeConversation) {
        this.createPauseEvent(this.activeConversation.id, PauseReason.Switch, timestamp);
      }

      this.createResumeEvent(conversation, timestamp);
      this.activeConversation = conversation;
      this.saveToStorage();
    } else if (!isTrackable && alreadyResumed) {
      this.activeConversation = null;
      this.clearStoredActiveIfMatches(conversation.id);
      this.saveToStorage();
    }

    if (this.pendingEvents.length > 0) {
      taskFor(this.flushEventsTask).perform();
    }
  }

  @task({ drop: true })
  *flushEventsTask(): TaskGenerator<void> {
    if (this.pendingEvents.length === 0) {
      return;
    }

    try {
      yield this.persistEvents();
      this.clearEventsStorage();
      console.log('[AHT] Events flushed successfully');
    } catch (error) {
      console.error('[AHT] Failed to flush events', error);
      this.logService.log(error);
    }
  }

  private async persistEvents(): Promise<void> {
    let apiEvents = this.pendingEvents.map((e) => ({
      conversation_id: e.conversationId,
      type: e.type,
      timestamp: e.timestamp.toISOString(),
      reason: e.reason,
    }));

    await postRequest(`/ember/inbox/handling_events/batch?app_id=${this.session.workspace.id}`, {
      events: apiEvents,
    });
  }

  private flushEventsViaSendBeacon(): void {
    if (this.pendingEvents.length === 0) {
      return;
    }

    let csrfToken =
      document.querySelector("meta[name='csrf-token']")?.getAttribute('content') || '';

    let payload = JSON.stringify({
      authenticity_token: csrfToken,
      events: this.pendingEvents.map((event) => ({
        conversation_id: event.conversationId,
        type: event.type,
        timestamp: event.timestamp.toISOString(),
        reason: event.reason,
      })),
    });

    let url = `/ember/inbox/handling_events/batch?app_id=${this.session.workspace.id}`;

    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'conversation-handling-service': ConversationHandlingService;
  }
}
